import { getClient } from '@api/client';
import { ServiceUserUtils } from '../../lib';
import ServiceUser from '../../types/ServiceUser';
import { getUsersQuery } from './getServiceUsersQuery';

async function getUsers(currentUserId: string): Promise<ServiceUser[]> {
  const { data, errors } = await getClient().query({
    query: getUsersQuery(),
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  // temporary error handling to minimize retries CCP-390
  // we don't want to throw an error if there are a small amount of
  // status errors and the current user status is successful
  if (errors) {
    const currentUserIndex: number | undefined = data?.users?.items?.findIndex(
      (user) => user.id === currentUserId,
    );

    const statusErrors = errors.filter(
      (error) =>
        error.path &&
        error.path.length === 4 &&
        (error.path[3] === 'stats' ||
          error.path[3] === 'status' ||
          error.path[3] === 'available'),
    );

    // unique status errors by user index
    const uniqueStatusErrors = statusErrors.filter(
      (error1, index, array) =>
        array.findIndex((error2) => error1.path![2] === error2.path![2]) ===
        index,
    );

    // see if our current user has a status error
    const currentUserHasStatusError = uniqueStatusErrors.some((error) => {
      return error.path![2] === currentUserIndex;
    });

    // calc percentage of users with status errors
    const statusErrorPercentage =
      uniqueStatusErrors.length /
      Math.max(data?.users?.items?.length ?? 0, uniqueStatusErrors.length);

    // error out if we have non-status errors
    if (errors.length > statusErrors.length) {
      throw new Error(errors[0].message);
    }

    // error out if more than 30% of our users report status errors
    if (uniqueStatusErrors.length > 0 && statusErrorPercentage > 0.3) {
      throw new Error('Failed to get user statuses');
    }

    // error out if our current user has a status error
    if (currentUserHasStatusError) {
      throw new Error('Failed to get user status for current user');
    }
  }

  const users =
    data?.users?.items?.map((user: any) => {
      const availabilityActivityStatusData =
        ServiceUserUtils.parseAvailabilityActivityStatus(user);
      const {
        status: _status,
        availabilityActivityStatus: _availabilityActivityStatus,
        ...rest
      } = user;
      return {
        ...rest,
        capacity: 1,
        ...availabilityActivityStatusData,
      };
    }) ?? [];
  return users;
}

export default getUsers;
